import React, { ReactNode, useEffect, useRef, useState } from "react";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import { Icons } from "atoms/constants/icons";
import { ButtonStyles } from "atoms/constants/button-styles";
import Paragraph from "atoms/typography/paragraph";
import { ParagraphSizes } from "atoms/constants/paragraph-sizes";
import ChatModalResults from "organisms/modals/chat-modal/chat-results";
import ChatPrompt from "organisms/modals/chat-modal/chat-prompt";
import { useConversationActions } from "utilities/atoms/conversations/current-conversation/use-conversation-actions.hook";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import Tooltip, { TooltipPlacement } from "molecules/tooltips/tooltip";
import ChatOfflineTooltipContent from "organisms/modals/chat-modal/chat-offline-tooltip-content";
import { ChatContextChip } from "molecules/chat-context/chat-context-chip";
import { ChatContextType } from "constants/chat-context-types";
import { useEvents } from "utilities/contexts/events/events-provider";
import { useConversation } from "utilities/atoms/conversations/current-conversation/use-conversation.hook";
import { ConversationEventActions } from "models/constants/events/chat-event-actions";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import CasiChatBookviewRefAtom from "utilities/atoms/casi-chat-bookview-ref-atom";
import { useSetAtom } from "jotai";

const BASE_CLASS_NAME = "c-chat-panel";
interface ChatPanelContentProps {
    context: ChatContextType;
    contextList: ReactNode[];
    applicationContext?: ConversationApplicationContextRecord;
}
const ChatPanelContent: React.FC<ChatPanelContentProps> = ({
    context,
    contextList,
    applicationContext,
}: ChatPanelContentProps) => {
    const { currentConversation } = useConversation();
    const { clearConversation } = useConversationActions();
    const { createConversationEvent } = useEvents();
    const { isOnline } = useNetworkInformation();
    const [prompt, setPrompt] = useState("");
    const [newChatStarted, setNewChatStarted] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const offlineClassName = !isOnline ? "-offline" : "";
    const setCasiChatBookviewRefAtom = useSetAtom(CasiChatBookviewRefAtom);

    useEffect(() => {
        setCasiChatBookviewRefAtom(containerRef);
    }, [setCasiChatBookviewRefAtom]);

    const handleClearChat = () => {
        if (!isOnline) {
            return;
        }

        const conversationId = currentConversation?.id;
        if (conversationId) {
            createConversationEvent(
                conversationId,
                ConversationEventActions.BookViewHistoryCleared
            );
        }

        clearConversation();
        setPrompt("");
        setNewChatStarted(true);
    };

    const handleContextChipClickEvent = () => {
        if (currentConversation?.id) {
            createConversationEvent(
                currentConversation.id,
                ConversationEventActions.ConversationContextChipClicked
            );
        }
    };

    return (
        <div className={`${BASE_CLASS_NAME} ${offlineClassName}`}>
            <div className={`${BASE_CLASS_NAME}__header`}>
                <Tooltip
                    disabled={isOnline}
                    content={<ChatOfflineTooltipContent />}
                    durationInMs={0}
                    hideOnClick={true}
                    placement={TooltipPlacement.AutoStart}>
                    <Button
                        cssClassName={`${BASE_CLASS_NAME}__button`}
                        style={ButtonStyles.Text}
                        onClick={handleClearChat}
                        aria-disabled={!isOnline}>
                        <Icon
                            cssClassName={`${BASE_CLASS_NAME}__icon`}
                            type={Icons.AutoRenew}
                        />
                        <Paragraph size={ParagraphSizes.XSmall}>
                            Clear
                        </Paragraph>
                    </Button>
                </Tooltip>
            </div>

            <div className={`${BASE_CLASS_NAME}__content`} ref={containerRef}>
                <ChatModalResults
                    containerRef={containerRef}
                    isBookview={true}
                    applicationContext={applicationContext}
                    includeConversationId={
                        // Inlcuding the conversationId will load the conversation in bookview when navigating to a citation
                        applicationContext?.currentView?.type === "Notebooks"
                    }
                />
                <div className={`${BASE_CLASS_NAME}__prompt-wrapper`}>
                    <ChatContextChip
                        onClick={handleContextChipClickEvent}
                        context={context}
                        contextList={contextList}
                    />
                    <ChatPrompt
                        newChatStarted={newChatStarted}
                        prompt={prompt}
                        handleSetPrompt={setPrompt}
                        applicationContext={applicationContext}
                    />
                </div>
            </div>
        </div>
    );
};

export default ChatPanelContent;
